import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  updateAccountStatus,
  getAccount,
  getAgedAccounts,
  uploadNotes,
  getEnergyImpact,
  getLinkedAccounts,
  getAccountCorrespondences,
  getAccountEvents,
  getAccountNotifications,
  getAccountCorrespondenceLetter,
  getWelcomeKit,
  getAccountNumberHistory,
  updateAccountNumberHistory,
  updateAccountName,
  getAccountPromos,
  addPromo,
  getAccountOrdersList,
  getAccountOrderAuthorizations,
  getAccountTPVS,
  getAccountPayableBills,
  getAccountContractHistories,
  getAccountBillImages,
  getAccountServiceHistories,
  voidAndReissueCheck,
  saveAccountAddress,
  delinkAccount,
  inboundDropAccount,
  updateAccountEmail,
  activateAccount,
  setAccountPrimaryPhoneNumber,
  getOfferChangeDetails,
  dropOffer,
  getRejectByAccountId,
  getRejectById,
  getDropReasons,
  requestAccountDrop,
  saveRejectInformation,
  getAccountExists,
  getRewardsByAccount,
  getAccountReferrals,
  addReferral,
} from "@apis/accounts/accounts"
import type {
  IAccountExistsParams,
  IAccount,
  ICorrespondence,
} from "@apis/accounts/types"
import { accountKeys } from "@apis/accounts/queryKeys"
import { useTransformAccountData } from "@apis/accounts/dataTransformations"

import type { IAccountPromo, IAccountRewards } from "@apis/rewards/types"
import { transformRewardsData } from "@apis/rewards/dataTransformations"
import { getAccounts } from "@apis/prospects/prospects"
import type { IMemberSearchParams } from "@apis/prospects/types"
import { RAF_TEMPLATES } from "@apis/accounts/utils"
import { CORRESPONDENCE_TYPES } from "@constants"

export const useUploadNotes = () => {
  return useMutation({
    mutationFn: uploadNotes,
  })
}

export const useAgedAccounts = () => {
  return useQuery({
    queryKey: accountKeys.agedAccounts(),
    queryFn: () => getAgedAccounts(),
  })
}

export const useAccount = <TSelected = IAccount>(
  account_id: string,
  select?: (data: IAccount) => TSelected,
) => {
  return useQuery({
    queryKey: accountKeys.detail(account_id),
    queryFn: () => getAccount(account_id),
    select,
  })
}

export const useUpdateAccountStatusMutation = () => {
  return useMutation({
    mutationFn: updateAccountStatus,
  })
}

export const useUpdateAccountNameMutation = () => {
  return useMutation({
    mutationFn: updateAccountName,
  })
}

export const useUpdateAccountEmailMutation = () => {
  return useMutation({
    mutationFn: updateAccountEmail,
  })
}

export const useSerializedAccountData = (account_id: string) => {
  return useAccount(account_id, useTransformAccountData)
}

export const useGetEnergyImpactQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.energyImpact(account_id),
    queryFn: () => getEnergyImpact(account_id),
  })
}

export const useGetLinkedAccountsQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.linkedAccounts(account_id),
    queryFn: () => getLinkedAccounts(account_id),
  })
}

export const useGetAccountOrdersList = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.ordersList(account_id),
    queryFn: () => getAccountOrdersList(account_id),
    select: (data) => {
      return data.sort((a, b) =>
        b.order_dt < a.order_dt ? -1 : b.order_dt > a.order_dt ? 1 : 0,
      )
    },
  })
}

export const useGetAccountCorrespondencesQuery = <
  TSelected = ICorrespondence[],
>(
  account_id: string,
  select?: (data: ICorrespondence[]) => TSelected,
) => {
  return useQuery({
    queryKey: accountKeys.accountCorrespondences(account_id),
    queryFn: () => getAccountCorrespondences(account_id),
    select,
  })
}

export const useComputedAccountCorrespondencesQuery = (account_id: string) => {
  return useGetAccountCorrespondencesQuery(account_id, (data) => {
    return data.map((correspondence) => {
      const isWelcome =
        correspondence.correspondence_type_code === CORRESPONDENCE_TYPES.WELCOME

      return {
        ...correspondence,
        description: isWelcome
          ? "Welcome Letter & Terms"
          : correspondence.correspondence_type_code,
        isWelcome: isWelcome,
      }
    })
  })
}

export const useGetAccountEventsQuery = (
  prospect_guid: string | undefined,
  account_id: string | undefined,
  isEnabled: boolean = true,
) => {
  return useQuery({
    queryKey: accountKeys.accountEvents(prospect_guid, account_id),
    queryFn:
      isEnabled && prospect_guid && account_id
        ? () => getAccountEvents(prospect_guid, account_id)
        : skipToken,
  })
}

export const useGetAccountNotificationsQuery = ({
  prospect_guid,
  account_id,
}: {
  prospect_guid: string | undefined
  account_id: string
}) => {
  return useQuery({
    queryKey: accountKeys.accountNotifications(prospect_guid, account_id),
    queryFn: prospect_guid
      ? () => getAccountNotifications(prospect_guid, account_id)
      : skipToken,
  })
}

export const useGetAccountCorrespondenceLetterQuery = ({
  account_id,
  account_correspondence_id,
}: {
  account_id: string
  account_correspondence_id: number
}) => {
  return useQuery({
    queryKey: accountKeys.accountCorrespondenceLetter(
      account_id,
      account_correspondence_id,
    ),
    queryFn: () =>
      getAccountCorrespondenceLetter({ account_id, account_correspondence_id }),
    enabled: false,
    retry: false,
  })
}

export const useGetWelcomeKitQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.welcomeKit(account_id),
    queryFn: () => getWelcomeKit(account_id),
    enabled: false,
    retry: false,
  })
}

export const useGetAccountNumberHistoryQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.accountNumberHistory(account_id),
    queryFn: () => getAccountNumberHistory(account_id),
  })
}

export const useGetOfferChangeDetailsQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.offerChangeDetails(account_id),
    queryFn: () => getOfferChangeDetails(account_id),
  })
}

export const useUpdateAccountNumberHistoryMutation = () => {
  return useMutation({
    mutationFn: updateAccountNumberHistory,
  })
}

export const useSetAccountPrimaryPhoneNumberMutation = () => {
  return useMutation({
    mutationFn: setAccountPrimaryPhoneNumber,
  })
}

export const useGetAccountPromosQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.accountPromos(account_id),
    queryFn: () => getAccountPromos(account_id),
  })
}

export const useAddPromo = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addPromo,

    onMutate: async ({ account_id, data }) => {
      const key = accountKeys.accountPromos(account_id)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<IAccountPromo[]>(key)

      if (previous) {
        const placeholder: IAccountPromo = {
          account_promo_id: Date.now() * -1,
          account_id: Number(account_id),
          promo_code: data.promo_code,
          reason_code: data.reason_code,
          promo: {
            promo_code: data.promo_code,
            promo_name: "",
            promo_desc: "",
          },
        }
        queryClient.setQueryData<IAccountPromo[]>(key, [
          ...previous,
          placeholder,
        ])
      }

      return { previous }
    },

    onError: (_err, _vars, context) => {
      const { previous } = context || {}
      if (previous) {
        const key = accountKeys.accountPromos(_vars.account_id)
        queryClient.setQueryData(key, previous)
      }
    },

    onSuccess: (newData, vars) => {
      const key = accountKeys.accountPromos(vars.account_id)
      queryClient.setQueryData(key, newData)
    },

    onSettled: (_data, _error, vars) => {
      const key = accountKeys.accountPromos(vars.account_id)
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export const useGetAccountOrderAuthorizationsQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.orderAuthorizations(account_id),
    queryFn: () => getAccountOrderAuthorizations(account_id),
  })
}

export const useGetAccountTPVSQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.accountTPVs(account_id),
    queryFn: () => getAccountTPVS(account_id),
  })
}

export const useGetAccountPayableBillsQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.accountPayableBills(account_id),
    queryFn: () => getAccountPayableBills(account_id),
  })
}

export const useVoidAndReissueCheckMutation = () => {
  return useMutation({
    mutationFn: voidAndReissueCheck,
  })
}

export const useGetAccountContractHistoriesQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.contractHistories(account_id),
    queryFn: () => getAccountContractHistories(account_id),
  })
}

export const useGetAccountBillImagesQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.billImages(account_id),
    queryFn: () => getAccountBillImages(account_id),
  })
}

export const useGetAccountServiceHistoriesQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.serviceHistories(account_id),
    queryFn: () => getAccountServiceHistories(account_id),
  })
}

export const useUpdateAccountAddressMutation = () => {
  return useMutation({
    mutationFn: saveAccountAddress,
  })
}

export const useDelinkAccountMutation = () => {
  return useMutation({
    mutationFn: delinkAccount,
  })
}

export const useInboundDropAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: inboundDropAccount,
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(data.account_id),
      })
    },
  })
}

export const useActivateAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: activateAccount,
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(data.account_id),
      })
    },
  })
}

export const useDropOfferMutation = () => {
  return useMutation({
    mutationFn: dropOffer,
  })
}

export const useGetRejectByAccountQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.accountRejects(account_id),
    queryFn: () => getRejectByAccountId(account_id),
  })
}

export const useGetRejectQuery = ({
  account_id,
  reject_id,
}: {
  account_id: string
  reject_id: string | undefined
}) => {
  return useQuery({
    queryKey: accountKeys.accountRejectInformation(account_id, reject_id),
    queryFn: reject_id ? () => getRejectById(reject_id) : skipToken,
  })
}

export const useGetDropReasonsQuery = () => {
  return useQuery({
    queryKey: accountKeys.dropReasons(),
    queryFn: () => getDropReasons(),
  })
}

export const useRequestAccountDropMutation = () => {
  return useMutation({
    mutationFn: requestAccountDrop,
  })
}

export const useSaveRejectMutation = () => {
  return useMutation({
    mutationFn: saveRejectInformation,
  })
}

export const useGetAccountExistsQuery = (params?: IAccountExistsParams) => {
  return useQuery({
    queryKey: accountKeys.accountExists(params),
    queryFn: params ? () => getAccountExists(params) : skipToken,
  })
}

export const useGetAccountReferralsQuery = (account_id: string) => {
  return useQuery({
    queryKey: accountKeys.accountReferrals(account_id),
    queryFn: () => getAccountReferrals(account_id),
  })
}

export const useAddReferralMutation = () => {
  return useMutation({
    mutationFn: addReferral,
  })
}

export const useGetRewardsByAccountQuery = <TSelected = IAccountRewards>(
  account_id: string,
  select?: (data: IAccountRewards) => TSelected,
) => {
  return useQuery({
    queryKey: accountKeys.accountRewards(account_id),
    queryFn: () => getRewardsByAccount(account_id),
    select,
  })
}

export const useSerializedAccountRewardsData = (account_id: string) => {
  return useGetRewardsByAccountQuery(account_id, (data) =>
    transformRewardsData(data, account_id),
  )
}

export const useRafReward = (account_id: string) => {
  return useGetRewardsByAccountQuery(
    account_id,
    (data) =>
      data.account_rewards_by_unit
        .flatMap((unit) => unit.rewards)
        .find((r) => RAF_TEMPLATES.includes(r.template as string)) ?? null,
  )
}

export const useGetAccountsQuery = (
  params: IMemberSearchParams,
  isEnabled: boolean = true,
) => {
  return useQuery({
    queryKey: accountKeys.list(JSON.stringify(params)),
    queryFn:
      params.query && isEnabled
        ? ({ signal }) => getAccounts({ params, signal })
        : skipToken,
  })
}
